import React, { useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend
);

// Fixed-order categorical palette (validated for colorblind-safe adjacent contrast).
// "Other" is intentionally a neutral slate, not a categorical hue, since it is an
// aggregate bucket rather than a distinct identity.
const CATEGORY_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];
const OTHER_COLOR = '#94a3b8';
const TOP_N = 7;
const CHART_HEIGHT = 230;

const OPTIONS = [
    { value: 'polymerEntries', label: 'Polymers' },
    { value: 'solventEntries', label: 'Solvents' },
    { value: 'collectorTypes', label: 'Collector Types' },
    { value: 'needleTypes', label: 'Needle Types' },
    { value: 'all', label: 'All Charts (Overview)' },
];

// Collapse a long-tail category distribution to the top N entries + a single
// "Other" bucket, so a figure with dozens of unique values stays compact and legible.
const bucketize = (statObj, topN = TOP_N) => {
    const entries = Object.entries(statObj).sort((a, b) => b[1] - a[1]);
    const top = entries.slice(0, topN);
    const rest = entries.slice(topN);
    const restSum = rest.reduce((sum, [, v]) => sum + v, 0);

    const buckets = top.map(([label, value]) => ({ label, value, isOther: false }));
    if (restSum > 0) {
        buckets.push({ label: `Other (${rest.length})`, value: restSum, isOther: true });
    }
    return { buckets, uniqueCount: entries.length };
};

export default function DetailedCharts({ data }) {
    const [selectedView, setSelectedView] = useState('polymerEntries');

    // Process Data
    const stats = useMemo(() => {
        const polymerEntries = {};
        const solventEntries = {};
        const collectorTypes = {};
        const needleTypes = {};

        data.forEach(item => {
            // Polymer Names
            const polymer = item['Polymer Name(s)'] || '-';
            const polymerKey = (polymer !== '-' && polymer) ? polymer : 'Unspecified';
            polymerEntries[polymerKey] = (polymerEntries[polymerKey] || 0) + 1;

            // Solvent Names
            const solvent = item['Solvent Name(s)'] || '-';
            const solventKey = (solvent !== '-' && solvent) ? solvent : 'Unspecified';
            solventEntries[solventKey] = (solventEntries[solventKey] || 0) + 1;

            // Collector Type
            const collector = item['Collector Type'] || '-';
            const collectorKey = (collector !== '-' && collector) ? collector : 'Unspecified';
            collectorTypes[collectorKey] = (collectorTypes[collectorKey] || 0) + 1;

            // Needle Type
            const needle = item['Needle Type'] || '-';
            const needleKey = (needle !== '-' && needle) ? needle : 'Unspecified';
            needleTypes[needleKey] = (needleTypes[needleKey] || 0) + 1;
        });

        return { polymerEntries, solventEntries, collectorTypes, needleTypes };
    }, [data]);

    const createChartData = (statObj) => {
        const { buckets } = bucketize(statObj);
        const total = buckets.reduce((s, b) => s + b.value, 0);

        return {
            chartData: {
                labels: buckets.map(b => b.label),
                datasets: [
                    {
                        data: buckets.map(b => b.value),
                        backgroundColor: buckets.map((b, i) => b.isOther ? OTHER_COLOR : CATEGORY_COLORS[i % CATEGORY_COLORS.length]),
                        borderRadius: 4,
                        borderSkipped: false,
                        maxBarThickness: 20,
                    },
                ],
            },
            total,
        };
    };

    const buildOptions = (total) => ({
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false }, // single series — the title names it
            tooltip: {
                backgroundColor: '#003366',
                padding: 8,
                titleFont: { size: 12 },
                bodyFont: { size: 12 },
                callbacks: {
                    label: (ctx) => {
                        const pct = total > 0 ? ((ctx.parsed.x / total) * 100).toFixed(1) : 0;
                        return ` ${ctx.parsed.x} records (${pct}%)`;
                    },
                },
            },
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: { precision: 0, color: '#94a3b8', font: { size: 11 } },
                grid: { color: '#eef1f5' },
            },
            y: {
                ticks: { color: '#334155', font: { size: 11 } },
                grid: { display: false },
            },
        },
    });

    if (!data || data.length === 0) return null;

    const renderChart = (key, title) => {
        const { chartData, total } = createChartData(stats[key]);
        const uniqueCount = Object.keys(stats[key]).length;
        return (
            <div className="card border-0 shadow-sm h-100 chart-card-compact">
                <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-baseline mb-2">
                        <h6 className="mb-0 fw-bold text-dark">{title}</h6>
                        <small className="text-muted">
                            {uniqueCount > TOP_N ? `top ${TOP_N} of ${uniqueCount}` : `${uniqueCount} unique`}
                        </small>
                    </div>
                    <div style={{ height: `${CHART_HEIGHT}px` }}>
                        <Bar options={buildOptions(total)} data={chartData} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 fw-bold text-dark">Dataset Figures</h6>
                <div style={{ width: '210px' }}>
                    <select
                        className="form-select form-select-sm shadow-sm"
                        value={selectedView}
                        onChange={(e) => setSelectedView(e.target.value)}
                    >
                        {OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {selectedView === 'all' ? (
                <div className="row row-cols-1 row-cols-md-2 row-cols-xl-4 g-3">
                    {OPTIONS.filter(o => o.value !== 'all').map(opt => (
                        <div key={opt.value} className="col">
                            {renderChart(opt.value, opt.label)}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="row justify-content-center">
                    <div className="col-md-8 col-lg-6">
                        {renderChart(selectedView, OPTIONS.find(o => o.value === selectedView)?.label)}
                    </div>
                </div>
            )}
        </div>
    );
}
